import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Edit2, Trash2 } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { ModalStackProvider, useModalZIndex } from '../contexts/ModalStackContext';
import { getSnippets, deleteSnippet, Snippet } from '../lib/snippets';
import CreateSnippetModal from './CreateSnippetModal';
import toast from 'react-hot-toast';

interface SnippetsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectSnippet?: (snippet: Snippet) => void;
}

export default function SnippetsModal({
  isOpen,
  onClose,
  onSelectSnippet
}: SnippetsModalProps) {
  const { selectedBusiness } = useBusiness();
  const modalZ = useModalZIndex(100070);
  const [isAnimating, setIsAnimating] = useState(false);
  const [snippets, setSnippets] = useState<Snippet[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingSnippet, setEditingSnippet] = useState<Snippet | null>(null);

  // Handle animation states
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  // Load snippets when modal opens
  useEffect(() => {
    if (isOpen && selectedBusiness?.id) {
      loadSnippets();
    }
  }, [isOpen, selectedBusiness?.id]);

  const loadSnippets = async () => {
    if (!selectedBusiness?.id) return;
    
    setIsLoading(true);
    try {
      const data = await getSnippets(selectedBusiness.id);
      setSnippets(data);
    } catch (error) {
      console.error('Error loading snippets:', error);
      toast.error('Failed to load snippets');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSnippet = () => {
    setEditingSnippet(null);
    setIsCreateModalOpen(true);
  };

  const handleEditSnippet = (snippet: Snippet, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingSnippet(snippet);
    setIsCreateModalOpen(true);
  };

  const handleDeleteSnippet = async (snippetId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!confirm('Are you sure you want to delete this snippet?')) {
      return;
    }

    try {
      await deleteSnippet(snippetId);
      setSnippets(snippets.filter(s => s.id !== snippetId));
      toast.success('Snippet deleted');
    } catch (error) {
      console.error('Error deleting snippet:', error);
      toast.error('Failed to delete snippet');
    }
  };

  const handleSnippetSuccess = (snippet: Snippet) => {
    // Update or add snippet to list
    setSnippets(prevSnippets => {
      const existing = prevSnippets.find(s => s.id === snippet.id);
      if (existing) {
        // Update existing
        return prevSnippets.map(s => s.id === snippet.id ? snippet : s);
      } else {
        // Add new to beginning
        return [snippet, ...prevSnippets];
      }
    });
    toast.success(editingSnippet ? 'Snippet updated' : 'Snippet created');
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  return createPortal(
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{ zIndex: modalZ, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
      onClick={onClose}
    >
      <div 
        className={`bg-gray-50 transform transition-all duration-200 ${
          isMobile
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${isOpen ? 'translate-x-0' : 'translate-x-full'}`
        }`}
        style={{
          zIndex: modalZ,
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
        }}
        onTransitionEnd={handleTransitionEnd}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-3">
            <button
              onClick={onClose}
              className="h-9 w-9 flex items-center justify-center text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <X className="h-5 w-5" />
            </button>
            <h2 className="flex-1 text-lg font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
              Message Snippets
            </h2>
            <button
              onClick={handleCreateSnippet}
              className="h-9 w-9 flex items-center justify-center bg-blue-600 text-white rounded-full hover:bg-blue-700 active:bg-blue-800 active:scale-95 transition-all duration-150 shadow-sm"
              style={{ WebkitTapHighlightColor: 'transparent' }}
              aria-label="New snippet"
            >
              <Plus className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-500 text-sm">Loading snippets...</p>
                </div>
              </div>
            ) : snippets.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full px-6 py-12">
                <div className="text-center max-w-md">
                  <div className="mx-auto h-14 w-14 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-500 mb-4">
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2" style={{ letterSpacing: '-0.02em' }}>No snippets yet</h3>
                  <p className="text-sm text-gray-500 mb-6">
                    Create your first message snippet to save time when responding to customers.
                  </p>
                  <button
                    onClick={handleCreateSnippet}
                    className="inline-flex items-center gap-2 px-5 py-3 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98] transition-all duration-150 shadow-sm"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Plus className="h-4 w-4" />
                    Create First Snippet
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-4 space-y-2.5">
                {snippets.map((snippet) => (
                  <div
                    key={snippet.id}
                    onClick={() => {
                      onSelectSnippet?.(snippet);
                      onClose();
                    }}
                    className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm active:bg-gray-50 active:scale-[0.99] transition-all cursor-pointer"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 mb-1 truncate">{snippet.title}</h3>
                        <p className="text-sm text-gray-500 line-clamp-2">{snippet.content}</p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={(e) => handleEditSnippet(snippet, e)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 active:bg-blue-100 rounded-full transition-colors"
                          title="Edit snippet"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => handleDeleteSnippet(snippet.id, e)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 active:bg-red-100 rounded-full transition-colors"
                          title="Delete snippet"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Snippet Modal — nested under SnippetsModal in the stack */}
      {selectedBusiness && (
        <ModalStackProvider zIndex={modalZ}>
          <CreateSnippetModal
            isOpen={isCreateModalOpen}
            onClose={() => {
              setIsCreateModalOpen(false);
              setEditingSnippet(null);
            }}
            onSuccess={handleSnippetSuccess}
            businessId={selectedBusiness.id}
            editSnippet={editingSnippet}
          />
        </ModalStackProvider>
      )}
    </div>,
    document.body
  );
}

